import React, { useState } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    useTheme,
    IconButton,
    Box,
    Tooltip,
    Switch,
    FormControlLabel,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    DeleteForever as DeleteForeverIcon,
    Add as AddIcon,
} from '@material-ui/icons'

import { CompanyStatus } from '../../../models'
import { BillingGroup, PaymentSchedule } from '../../../models/Billing/types'
import { CompanyWithInvoiceCode } from '../Property/types'

interface BillingGroupModalProps {
    open: boolean
    billingGroup: BillingGroup | null
    propertyList: CompanyWithInvoiceCode[]
    onClose: () => void
    onSave: (
        billingGroupId: number,
        invoiceEmail: string,
        paymentSchedule: PaymentSchedule,
        active: boolean,
    ) => void
    onRemoveCompany?: (
        billingGroupId: number,
        company: CompanyWithInvoiceCode,
    ) => void
    onAddCompany?: (billingGroupId: number, companyId: number) => void
}

export const BillingGroupModal: React.FC<BillingGroupModalProps> = ({
    open,
    billingGroup,
    propertyList,
    onClose,
    onSave,
    onRemoveCompany,
    onAddCompany,
}) => {
    const theme = useTheme()
    const [invoiceEmail, setInvoiceEmail] = useState('')
    const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule>(
        PaymentSchedule.MONTHLY,
    )
    const [active, setActive] = useState(true)
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
    const [
        companyToRemove,
        setCompanyToRemove,
    ] = useState<CompanyWithInvoiceCode | null>(null)
    const [selectedPropertyToAdd, setSelectedPropertyToAdd] = useState<
        number | ''
    >('')

    const availableProperties = React.useMemo(() => {
        return propertyList.filter(
            (property) =>
                !billingGroup?.companies.some(
                    (company) => company.id === property.id,
                ) &&
                property.status === CompanyStatus.ACTIVE &&
                property.organization.id === billingGroup?.organization.id &&
                property.billing_group === null,
        )
    }, [propertyList, billingGroup])

    React.useEffect(() => {
        if (billingGroup) {
            setInvoiceEmail(billingGroup.invoice_email)
            setPaymentSchedule(billingGroup.payment_schedule)
            setActive(billingGroup.active)
        }
    }, [billingGroup])

    const handleSave = () => {
        if (billingGroup) {
            onSave(billingGroup.id, invoiceEmail, paymentSchedule, active)
            onClose()
        }
    }

    const handleClose = () => {
        if (billingGroup) {
            setInvoiceEmail(billingGroup.invoice_email)
            setPaymentSchedule(billingGroup.payment_schedule)
            setActive(billingGroup.active)
        }
        onClose()
    }

    const handleRemoveCompany = (company: CompanyWithInvoiceCode) => {
        setCompanyToRemove(company)
        setIsRemoveDialogOpen(true)
    }

    const handleConfirmRemove = () => {
        if (billingGroup && companyToRemove && onRemoveCompany) {
            onRemoveCompany(billingGroup.id, companyToRemove)
        }
        setIsRemoveDialogOpen(false)
        setCompanyToRemove(null)
    }

    const handleCancelRemove = () => {
        setIsRemoveDialogOpen(false)
        setCompanyToRemove(null)
    }

    const handleAddCompany = () => {
        if (billingGroup && selectedPropertyToAdd && onAddCompany) {
            onAddCompany(billingGroup.id, selectedPropertyToAdd as number)
            setSelectedPropertyToAdd('')
        }
    }

    if (!billingGroup) return null

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: 12,
                        maxHeight: '90vh',
                    },
                }}
            >
                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: theme.spacing(1, 3),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.default,
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                        }}
                    >
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {billingGroup.quickbooks_customer.display_name}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={active}
                                    onChange={(e) =>
                                        setActive(e.target.checked)
                                    }
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 500 }}
                                >
                                    Active
                                </Typography>
                            }
                            style={{ margin: 0 }}
                        />
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        style={{ color: theme.palette.text.secondary }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent style={{ padding: theme.spacing(3) }}>
                    <div style={{ marginBottom: theme.spacing(2) }}>
                        <Typography
                            variant="h6"
                            style={{
                                fontWeight: 600,
                                marginBottom: theme.spacing(2),
                                color: theme.palette.primary.main,
                            }}
                        >
                            Billing Information
                        </Typography>

                        <div style={{ display: 'flex', gap: theme.spacing(3) }}>
                            <TextField
                                label="Invoice Email"
                                value={invoiceEmail}
                                onChange={(e) =>
                                    setInvoiceEmail(e.target.value)
                                }
                                variant="outlined"
                                fullWidth
                                style={{ flex: 1 }}
                            />

                            <FormControl variant="outlined" style={{ flex: 1 }}>
                                <InputLabel>Payment Schedule</InputLabel>
                                <Select
                                    value={paymentSchedule}
                                    onChange={(e) =>
                                        setPaymentSchedule(
                                            e.target.value as PaymentSchedule,
                                        )
                                    }
                                    label="Payment Schedule"
                                >
                                    <MenuItem value={PaymentSchedule.MONTHLY}>
                                        Monthly
                                    </MenuItem>
                                    <MenuItem value={PaymentSchedule.ANNUALLY}>
                                        Annually
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </div>

                    <Divider style={{ margin: theme.spacing(2, 0) }} />

                    <div>
                        <Typography
                            variant="h6"
                            style={{
                                fontWeight: 600,
                                marginBottom: theme.spacing(2),
                                color: theme.palette.primary.main,
                            }}
                        >
                            Properties ({billingGroup.companies.length})
                        </Typography>

                        <TableContainer
                            component={Paper}
                            style={{
                                maxHeight: 400,
                                boxShadow: theme.shadows[1],
                            }}
                        >
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            style={{
                                                fontWeight: 600,
                                                backgroundColor:
                                                    theme.palette.grey[50],
                                            }}
                                        >
                                            Company Name
                                        </TableCell>
                                        <TableCell
                                            style={{
                                                fontWeight: 600,
                                                backgroundColor:
                                                    theme.palette.grey[50],
                                            }}
                                        >
                                            Address
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{
                                                fontWeight: 600,
                                                backgroundColor:
                                                    theme.palette.grey[50],
                                            }}
                                        >
                                            Billable Units
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{
                                                fontWeight: 600,
                                                backgroundColor:
                                                    theme.palette.grey[50],
                                            }}
                                        >
                                            Billable Rate
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{
                                                fontWeight: 600,
                                                backgroundColor:
                                                    theme.palette.grey[50],
                                            }}
                                        >
                                            Remove
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {billingGroup.companies.map((company) => (
                                        <TableRow key={company.id} hover>
                                            <TableCell
                                                style={{ fontWeight: 500 }}
                                            >
                                                {company.name}
                                            </TableCell>
                                            <TableCell>
                                                {company.primary_address ? (
                                                    <div>
                                                        <div>
                                                            {
                                                                company
                                                                    .primary_address
                                                                    .address1
                                                            }
                                                        </div>
                                                        <div
                                                            style={{
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .secondary,
                                                                fontSize:
                                                                    '12px',
                                                            }}
                                                        >
                                                            {
                                                                company
                                                                    .primary_address
                                                                    .city
                                                            }
                                                            ,{' '}
                                                            {
                                                                company
                                                                    .primary_address
                                                                    .state
                                                            }{' '}
                                                            {
                                                                company
                                                                    .primary_address
                                                                    .zip_code
                                                            }
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span
                                                        style={{
                                                            color:
                                                                theme.palette
                                                                    .text
                                                                    .secondary,
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        No address available
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <span
                                                    style={{
                                                        fontWeight: 600,
                                                        color:
                                                            theme.palette
                                                                .primary.main,
                                                    }}
                                                >
                                                    {
                                                        company.billable_unit_count
                                                    }
                                                </span>
                                            </TableCell>
                                            <TableCell align="center">
                                                <span
                                                    style={{
                                                        fontWeight: 600,
                                                        color:
                                                            theme.palette
                                                                .success.main,
                                                    }}
                                                >
                                                    $
                                                    {parseFloat(
                                                        company.billable_unit_rate,
                                                    ).toFixed(2)}
                                                </span>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Remove from billing group">
                                                    <IconButton
                                                        onClick={() =>
                                                            handleRemoveCompany(
                                                                company,
                                                            )
                                                        }
                                                        style={{
                                                            color:
                                                                theme.palette
                                                                    .text
                                                                    .secondary,
                                                        }}
                                                    >
                                                        <DeleteForeverIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    {availableProperties.length > 0 && (
                        <>
                            <Divider style={{ margin: theme.spacing(2, 0) }} />

                            <div>
                                <Typography
                                    variant="h6"
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: theme.spacing(2),
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    Add Property ({availableProperties.length}{' '}
                                    available)
                                </Typography>

                                <Paper
                                    style={{
                                        padding: theme.spacing(2),
                                        backgroundColor: theme.palette.grey[50],
                                        border: `1px solid ${theme.palette.grey[200]}`,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: theme.spacing(2),
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <FormControl
                                            variant="outlined"
                                            style={{ flex: 1 }}
                                        >
                                            <InputLabel>
                                                Select Property
                                            </InputLabel>
                                            <Select
                                                value={selectedPropertyToAdd}
                                                onChange={(e) =>
                                                    setSelectedPropertyToAdd(
                                                        e.target
                                                            .value as number,
                                                    )
                                                }
                                                label="Select Property"
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300,
                                                        },
                                                    },
                                                }}
                                            >
                                                {availableProperties.map(
                                                    (property) => (
                                                        <MenuItem
                                                            key={property.id}
                                                            value={property.id}
                                                            style={{
                                                                height: '48px',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width:
                                                                        '100%',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        fontWeight: 500,
                                                                        fontSize:
                                                                            '14px',
                                                                        lineHeight:
                                                                            '1.2',
                                                                    }}
                                                                >
                                                                    {
                                                                        property.name
                                                                    }
                                                                </div>
                                                                {property.primary_address && (
                                                                    <div
                                                                        style={{
                                                                            fontSize:
                                                                                '12px',
                                                                            color:
                                                                                theme
                                                                                    .palette
                                                                                    .text
                                                                                    .secondary,
                                                                            lineHeight:
                                                                                '1.2',
                                                                        }}
                                                                    >
                                                                        {
                                                                            property
                                                                                .primary_address
                                                                                .city
                                                                        }
                                                                        ,{' '}
                                                                        {
                                                                            property
                                                                                .primary_address
                                                                                .state
                                                                        }
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                        </FormControl>

                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddCompany}
                                            disabled={!selectedPropertyToAdd}
                                            style={{
                                                minWidth: '140px',
                                                height: '56px',
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Add Property
                                        </Button>
                                    </div>

                                    <Typography
                                        variant="body2"
                                        style={{
                                            marginTop: theme.spacing(1),
                                            color: theme.palette.text.secondary,
                                            fontSize: '12px',
                                        }}
                                    >
                                        Select a property from the dropdown
                                        above to add it to this billing group.
                                    </Typography>
                                </Paper>
                            </div>
                        </>
                    )}
                </DialogContent>

                <DialogActions
                    style={{
                        padding: theme.spacing(2, 3),
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.default,
                    }}
                >
                    <Button onClick={handleClose} color="default">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={!invoiceEmail.trim()}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Remove Company Confirmation Dialog */}
            <Dialog
                open={isRemoveDialogOpen}
                onClose={handleCancelRemove}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: 12,
                    },
                }}
            >
                <DialogTitle style={{ padding: theme.spacing(2, 3) }}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Remove Property
                    </Typography>
                </DialogTitle>
                <DialogContent style={{ padding: theme.spacing(0, 3, 2, 3) }}>
                    <Typography variant="body1">
                        Are you sure you want to remove{' '}
                        <strong>{companyToRemove?.name}</strong> from this
                        billing group?
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: theme.spacing(2, 3) }}>
                    <Button onClick={handleCancelRemove} color="default">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmRemove}
                        color="secondary"
                        variant="contained"
                        style={{ backgroundColor: theme.palette.error.main }}
                    >
                        Remove Property
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
